'use client';

import { useEffect, useState } from 'react';

import { makeDebug } from '@ps/functions/utils/debug';
import { produce } from 'immer';
import {
  BehaviorSubject,
  type Subject,
  bufferTime,
  debounceTime,
  filter,
  interval,
  skip,
} from 'rxjs';

import { isChatDebugEnabled } from '../functions/debug';
import type { ChatStorageManager } from '../functions/storage/ChatStorageManager';
import type { ChatEvent } from '../types/ChatEvent';
import { type ClientChatMessage, MsgSendStatus } from '../types/ChatMessage';

const debug = makeDebug(
  'chat:pendingMessages',
  isChatDebugEnabled('pendingMessages'),
);

export const kDefaultMessageExpireTime = 120 * 1000; // 2 minutes
export const kMessageExpireCheckInterval = 10 * 1000; // 10 seconds

/**
 * Drives the pending-messages BehaviorSubject in response to chat
 * events (`sending-message`, `send-message-success/failed`,
 * `messages-loaded`, `remove-pending-message`) and an interval-based
 * expiration sweep. Persists to the supplied storage manager on a
 * 1-second debounce.
 */
export function useChatPendingMessagesManager(
  events$: Subject<ChatEvent>,
  storage: ChatStorageManager,
  currentUserId: string | null | undefined,
): { pendingMessages$: BehaviorSubject<ClientChatMessage[]> } {
  const [pendingMessages$, setSubject] = useState(
    () => new BehaviorSubject<ClientChatMessage[]>([]),
  );
  useEffect(() => {
    setSubject(new BehaviorSubject<ClientChatMessage[]>([]));
  }, [currentUserId]);

  useEffect(() => {
    void storage.getPendingMessages().then(messages => {
      pendingMessages$.next(messages);
    });

    const sub = pendingMessages$
      .pipe(skip(1), debounceTime(1000))
      .subscribe(messages => {
        void storage.savePendingMessages(messages).then(() => {
          debug('pending messages saved to local cache');
        });
      });
    return () => sub.unsubscribe();
  }, [pendingMessages$, storage]);

  useEffect(() => {
    const sub = events$
      .pipe(
        bufferTime(1000),
        filter(x => x.length > 0),
      )
      .subscribe(batch => {
        const current = pendingMessages$.getValue();
        const next = produce(current, draft => {
          for (const event of batch) {
            debug('handling chat event', event.type);
            switch (event.type) {
              case 'sending-message': {
                const i = draft.findIndex(
                  x => x.clientId === event.message.clientId,
                );
                if (i >= 0) draft[i] = event.message;
                else draft.push(event.message);
                break;
              }
              case 'send-message-success': {
                const i = draft.findIndex(
                  x => x.clientId === event.message.clientId,
                );
                if (i >= 0) draft.splice(i, 1);
                break;
              }
              case 'send-message-failed': {
                const i = draft.findIndex(
                  x => x.clientId === event.message.clientId,
                );
                if (i >= 0) {
                  console.error(event.error);
                  draft[i].sendStatus = MsgSendStatus.fail;
                  draft[i].lastSendErrorMsg =
                    typeof event.error === 'string'
                      ? event.error
                      : ((event.error as Error)?.message ?? 'Unknown error');
                }
                break;
              }
              case 'messages-loaded': {
                const ids = new Set(event.messages.map(x => x.clientId));
                const matches = current
                  .map(x => x.clientId)
                  .filter(id => ids.has(id));
                for (const clientId of matches) {
                  const i = draft.findIndex(x => x.clientId === clientId);
                  if (i >= 0) {
                    debug('removing pending message at index', i);
                    draft.splice(i, 1);
                  }
                }
                break;
              }
              case 'remove-pending-message': {
                const i = draft.findIndex(
                  x => x.clientId === event.message.clientId,
                );
                if (i >= 0) {
                  draft.splice(i, 1);
                  events$.next({
                    type: 'pending-message-removed',
                    message: event.message,
                  });
                }
                break;
              }
            }
          }
        });
        if (current !== next) {
          debug('pending messages updated');
          pendingMessages$.next(next);
        }
      });
    return () => sub.unsubscribe();
  }, [events$, pendingMessages$]);

  useEffect(() => {
    const sub = interval(kMessageExpireCheckInterval).subscribe(() => {
      const current = pendingMessages$.getValue();
      const expiredIds = new Set<string>();
      const next = produce(current, draft => {
        for (const message of draft) {
          if (
            message.sendStatus === MsgSendStatus.sending &&
            Date.now() - message.lastSendTime > kDefaultMessageExpireTime
          ) {
            message.sendStatus = MsgSendStatus.fail;
            message.lastSendErrorMsg = 'Timeout';
            expiredIds.add(message.clientId);
            debug('pending message expired:', message.clientId);
          }
        }
      });
      if (current !== next) {
        pendingMessages$.next(next);
        const expired = next.filter(x => expiredIds.has(x.clientId));
        events$.next({ type: 'messages-expired', messages: expired });
      }
    });
    return () => sub.unsubscribe();
  }, [events$, pendingMessages$]);

  return { pendingMessages$ };
}
